// Copyright ©️ 2026 eVolve MEP, LLC

import { useImperativeHandle, useRef, useState, useEffect } from 'react';

import { Combobox, InputBase, getDefaultZIndex, type Primitive } from '@mantine/core';
import { useElementSize, useUncontrolled } from '@mantine/hooks';

import { CreatableModal } from 'components/Mantine/TypeSafeSelect/Creatables/CreatableModal';
import { createdKey, creatorKey } from 'components/Mantine/TypeSafeSelect/Creatables/useCreatable';
import { InlineLabelSection, InlineLabelValue } from 'components/Mantine/TypeSafeSelect/InlineLabel';
import { SelectDropdown } from 'components/Mantine/TypeSafeSelect/SelectDropdown';
import { SelectValue } from 'components/Mantine/TypeSafeSelect/SelectValue';
import { useWrappedSelect } from 'components/Mantine/TypeSafeSelect/useWrappedSelect';
import { isNotNil } from 'helpers/isNotNil';
import { weakOmit } from 'helpers/omit';

import { getSelectOptionLabel } from './common';
import type { WrappedSelectProps } from './types';

export const WrappedSelect = <T extends Primitive, D extends object = object>(props: WrappedSelectProps<T, D>) => {
  const {
    className,
    classNames,
    label,
    inlineLabel = false,
    size,
    placeholder,
    nothingFoundMessage = 'No results.',
    includeCloseButton = false,
    clearSearchOnChange,
    leftSection,
    blurOnChange = false,
    comboboxProps,
    footer,
    loading,
    disabled,
    maxDropdownHeight,
    autoDropdownWidth,
    onScrollToEnd,
    onKeyDown,
    onBlur,
    ref: parentRef,
    value: parentValue,
    onChange: parentOnChange,
    defaultValue: parentDefaultValue = null,
    createdValue,
    withinPortal,
    // Manually excluded vars from `rest`, since they are handled in hooks
    clearable: _1,
    ...rest
  } = props;

  const ref = useRef<HTMLInputElement>(null);
  useImperativeHandle(parentRef, () => ref.current as HTMLInputElement, [ref]);

  const { ref: inlineLabelRef, width: inlineLabelWidth } = useElementSize<HTMLDivElement>();

  const [__value, __onChange] = useUncontrolled<T | null>({
    value: parentValue,
    defaultValue: parentDefaultValue,
    onChange: parentOnChange,
  });
  const [shouldCloseDropdown, setShouldCloseDropdown] = useState(false);
  const { combobox, options, searchPlaceholder, __onSearchChange, selectRightSection, overrideKeys, overrideProps } =
    useWrappedSelect(
      {
        type: 'single',
        __value,
        __onChange,
      },
      props,
    );
  const { data, onOptionSubmit, searchable, creationModalProps } = overrideProps;

  const selectedLabel = (createdValue || (isNotNil(__value) ? getSelectOptionLabel(data, __value) : null)) ?? null;
  const inputText = (combobox.dropdownOpened ? searchPlaceholder : selectedLabel) ?? '';

  /*
   * If the user creates another option with a modal, close the dropdown once the modal is closed.
   * Usually this will be because the user created and chose the new option
   * It does however have the downside that if the user cancels the creation modal,
   * the dropdown will still close without an option being chosen.
   * This useState variable is needed b/c the modal returns focus to the input on close, which re-opens the dropdown via `onFocus`
   * *after* CreatableModal.onClose runs, so keep checking on subsequent renders until we catch it open.
   */
  useEffect(() => {
    if (shouldCloseDropdown && combobox.dropdownOpened) {
      combobox.closeDropdown();
      setShouldCloseDropdown(false);
    }
  }, [combobox, shouldCloseDropdown]);

  return (
    <Combobox
      classNames={classNames}
      size={size}
      width={autoDropdownWidth ? 'auto' : undefined}
      {...comboboxProps}
      onOptionSubmit={(val) => {
        onOptionSubmit(val as T);
        if (val !== creatorKey && val !== createdKey) {
          __onChange(val as T);
        }
        combobox.closeDropdown();
        if (blurOnChange) {
          ref.current?.blur();
        }
        if (clearSearchOnChange || val === creatorKey || val === createdKey) {
          __onSearchChange('');
        }
      }}
      store={combobox}
      withinPortal={withinPortal}
      // Bring up one level so any select inside of a popover should render properly
      zIndex={getDefaultZIndex('popover') + 1}
    >
      <Combobox.Target>
        {searchable ? (
          <InputBase
            ref={ref}
            size={size}
            className={className}
            leftSection={
              inlineLabel ? (
                <InlineLabelSection ref={inlineLabelRef} label={label} leftSection={leftSection} />
              ) : (
                leftSection
              )
            }
            leftSectionWidth={inlineLabel ? inlineLabelWidth : undefined}
            leftSectionPointerEvents={inlineLabel ? 'none' : undefined}
            placeholder={placeholder}
            value={inputText}
            onMouseEnter={({ currentTarget: el }) => {
              const truncated = el.scrollWidth > el.clientWidth;
              // oxlint-disable-next-line evolve/no-param-reassign
              el.title = truncated ? inputText : '';
            }}
            onChange={(event) => {
              combobox.updateSelectedOptionIndex();
              __onSearchChange(event.currentTarget.value);
            }}
            w={inlineLabel ? 'fit-content' : undefined}
            // The label eats into the input's own width, so keep room to type next to it
            miw={
              inlineLabel
                ? 'calc(var(--input-left-section-width, 0px) + 10ch)'
                : (rest.miw ?? rest.maw ?? rest.w ?? 190)
            }
            {...weakOmit(rest, overrideKeys)}
            {...selectRightSection}
            disabled={disabled}
            onKeyDown={(e) => {
              combobox.openDropdown();
              onKeyDown?.(e);
            }}
            onClick={() => combobox.openDropdown()}
            onFocus={(...e) => {
              rest.onFocus?.(...e);
              combobox.openDropdown();
            }}
            onBlur={(...e) => {
              onBlur?.(...e);
              combobox.closeDropdown();
              __onSearchChange('');
            }}
            label={inlineLabel ? undefined : label}
          />
        ) : (
          <InputBase
            // @ts-expect-error; We can just pretend it's always an HTMLInputRef
            ref={ref}
            label={inlineLabel ? undefined : label}
            size={size}
            className={className}
            leftSection={leftSection}
            pointer
            w={inlineLabel ? 'fit-content' : undefined}
            miw={inlineLabel ? undefined : (rest.miw ?? rest.maw ?? rest.w ?? 200)}
            styles={{
              input: {
                textWrap: 'nowrap',
                overflowX: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
            {...weakOmit(rest, overrideKeys)}
            {...selectRightSection}
            style={{
              overflowX: 'hidden',
              // TODO: `.style` can now be a function, need to handle it
              // oxlint-disable-next-line typescript/no-misused-spread
              ...rest.style,
            }}
            component="button"
            type="button"
            disabled={disabled}
            onClick={() => combobox.openDropdown()}
            onBlur={() => combobox.closeDropdown()}
            onFocus={() => combobox.openDropdown()}
          >
            {inlineLabel ? (
              <InlineLabelValue label={label} selectedLabel={selectedLabel} placeholder={placeholder} />
            ) : (
              <SelectValue
                selectedLabel={isNotNil(__value) && !combobox.dropdownOpened ? inputText : null}
                placeholder={placeholder}
              />
            )}
          </InputBase>
        )}
      </Combobox.Target>

      <SelectDropdown
        ref={ref}
        combobox={combobox}
        options={options}
        footer={footer}
        nothingFoundMessage={nothingFoundMessage}
        maxDropdownHeight={maxDropdownHeight}
        includeCloseButton={includeCloseButton}
        loading={loading}
        autoDropdownWidth={autoDropdownWidth}
        onScrollToEnd={onScrollToEnd}
        error={rest.error}
      />

      <CreatableModal
        creatable={overrideProps.creatable}
        {...creationModalProps}
        onClose={() => {
          creationModalProps.onClose();
          setShouldCloseDropdown(true);
        }}
        onSubmit={(...v) => {
          creationModalProps.onSubmit(...v);
          creationModalProps.onClose();
        }}
      />
    </Combobox>
  );
};
